import {
  collection,
  doc,
  documentId,
  getDoc,
  getDocs,
  getFirestore,
  limit,
  query,
  Timestamp,
  where,
  WhereFilterOp,
  DocumentSnapshot as LiteDocumentSnapshot,
  QueryDocumentSnapshot,
} from "firebase/firestore/lite";
import { DocumentSnapshot } from "@firebase/firestore-types";
import { envVars } from "@ambii-com/core";
import { normalizeTimestampsInData } from "~/utils/fsJs";

let liteDb: ReturnType<typeof getFirestore> | null = null;

function getLiteDb() {
  if (!liteDb) {
    liteDb = getFirestore();
  }
  return liteDb;
}

function chunkArray<T>(list: T[], size: number): T[][] {
  const chunks: T[][] = [];
  for (let i = 0; i < list.length; i += size) {
    chunks.push(list.slice(i, i + size));
  }
  return chunks;
}

function normalizeLiteDocData(
  data: Record<string, unknown> | undefined
): Record<string, unknown> | undefined {
  if (!data) {
    return data;
  }
  return normalizeTimestampsInData(data);
}

/** Map Firestore Lite snapshot → compat shape for @ambii-com/core fromDoc */
function toCompatDocSnapshot(
  snap: LiteDocumentSnapshot | QueryDocumentSnapshot
): DocumentSnapshot {
  const raw = snap.data() as Record<string, unknown> | undefined;
  const data = normalizeLiteDocData(raw);
  return {
    id: snap.id,
    exists: snap.exists(),
    data: () => data,
    ref: undefined,
  } as DocumentSnapshot;
}

export async function liteGetDocument(
  collectionName: string,
  documentIdValue: string
): Promise<DocumentSnapshot> {
  const snap = await getDoc(doc(getLiteDb(), collectionName, documentIdValue));
  return toCompatDocSnapshot(snap);
}

type WhereClause = [field: string, op: WhereFilterOp, value: unknown];

export async function liteQueryCollection(
  collectionName: string,
  field: string,
  value: string
): Promise<DocumentSnapshot[]> {
  return liteQueryCollectionWith(collectionName, [field, "==", value]);
}

export async function liteQueryCollectionWith(
  collectionName: string,
  ...wheres: WhereClause[]
): Promise<DocumentSnapshot[]> {
  const constraints = wheres.map(([field, op, value]) => where(field, op, value));
  const q = query(collection(getLiteDb(), collectionName), ...constraints);
  const result = await getDocs(q);
  return result.docs.map(toCompatDocSnapshot);
}

export async function liteQueryCollectionLimited(
  collectionName: string,
  max: number,
  ...wheres: WhereClause[]
): Promise<DocumentSnapshot[]> {
  const constraints = wheres.map(([field, op, value]) => where(field, op, value));
  const q = query(collection(getLiteDb(), collectionName), ...constraints, limit(max));
  const result = await getDocs(q);
  return result.docs.map(toCompatDocSnapshot);
}

export async function liteGetDocumentsIn(
  collectionName: string,
  ids: string[]
): Promise<DocumentSnapshot[]> {
  if (ids.length === 0) {
    return [];
  }
  const chunks = chunkArray(ids, 10);
  const results = await Promise.all(
    chunks.map(async (chunk) => {
      const q = query(
        collection(getLiteDb(), collectionName),
        where(documentId(), "in", chunk)
      );
      const snap = await getDocs(q);
      return snap.docs.map(toCompatDocSnapshot);
    })
  );
  return results.flat();
}

export async function liteGetVacantSchedules(menuId: string): Promise<DocumentSnapshot[]> {
  const now = Timestamp.fromDate(new Date());
  return liteQueryCollectionWith(
    liteCollections.appointmentSchedules,
    ["receptionEndDatetime", ">", now],
    ["resourceId", "array-contains", menuId]
  );
}

export const liteCollections = {
  clinics: envVars.clinicCollectionName,
  forms: envVars.formsCollectionName,
  formConfigs: envVars.formsConfigCollectionName,
  formPages: envVars.pagesCollectionName,
  formQuestions: envVars.questionsCollectionName,
  appointmentMenus: envVars.appointmentMenusCollectionName,
  appointmentConfigs: envVars.appointmentConfigsCollectionName,
  appointmentSchedules: envVars.appointmentSchedulesCollectionName,
  scheduleAppointments: envVars.scheduleAppointmentCollectionName,
  patients: envVars.patientCollectionName,
  relatedPatients: envVars.relatedPatientCollectionName,
  admins: envVars.adminCollectionName,
  alleyPinConfigs: envVars.alleyPinConfigsCollectionName,
  vaccineConfigs: envVars.vaccineConfigCollectionName,
  vaccineMenuSettings: envVars.vaccineMenuSettingsCollectionName,
  vaccineAppointmentDetails: envVars.vaccineAppointmentDetailsCollectionName
} as const;
